import { useId, useLayoutEffect, useState, type FormEvent } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { useForm as useHookForm } from 'react-hook-form'

import { Button, Checkbox, TextInput } from '@atoms'
import { Accordion } from '@components/atoms/Accordion'
import Badge from '@components/atoms/Badge'
import { ConfigurationsLayout } from '@components/configurations/ConfigurationsLayout'
import { SettingsAccordionItem } from '@components/configurations/SettingsAccordionItem'
import { SettingsCard } from '@components/configurations/SettingsCard'
import { fieldErrors, SettingsForm, SettingsFormFooter, textField } from '@components/configurations/SettingsForm'
import { SettingsSwitches, type SettingSwitch } from '@components/configurations/SettingsSwitches'
import { applySidebarSurface } from '@components/layouts/sidebar/Sidebar'
import { type NavTree } from '@components/layouts/sidebar/navTree'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { RichTextEditorField } from '@components/molecules/RichTextEditorField'
import { SelectField, type SelectFieldOption } from '@components/molecules/SelectField'
import TagsSelect from '@components/molecules/TagsSelect'
import { scopedTranslation } from '@utils/I18n'
import { embedTheme, parseHex } from '@utils/embeds/theme'

const t = scopedTranslation('configurations.general')

type Values = {
  APP_NAME: string
  SIDEBAR_COLOR: string
  MINIMUM_TIME_BETWEEN_CONTACTS: string
  PRIMARY_SUPPORT_EMAIL: string
  PRIMARY_CONTACT_EMAILS: string
  COUNTRY_CODE: string
  '2FA_MODE': string
  TERMS_OF_USE_HEADLINE: string
  TERMS_OF_USE_DECLARATION: string
  TERMS_OF_USE_CONTENT: string
  DEFAULT_GROUP_PAGE_CONTENT: string
  UNSUBSCRIBE_LINK_TEXT: string
  UNSUBSCRIBE_PAGE_HEADLINE: string
  UNSUBSCRIBE_PAGE_CONTENT: string
}

type ImageKind = 'logo' | 'favicon' | 'background_image'

type LeaderRole = 'captain' | 'vice_captain' | 'admin'

type LeaderTagging = { enabled: boolean } & Record<LeaderRole, string[]>

export type GeneralShowProps = {
  values: Values
  images: Record<ImageKind, string | null>
  switches: SettingSwitch[]
  leaderTagging: LeaderTagging | null
  countries: SelectFieldOption<string>[]
  contactGaps: SelectFieldOption<string>[]
  admins: string[]
}

type ContentKey = Exclude<keyof Values, SiteKey>

type SiteKey = (typeof SITE_KEYS)[number]

const SITE_KEYS = [
  'APP_NAME',
  'SIDEBAR_COLOR',
  'MINIMUM_TIME_BETWEEN_CONTACTS',
  'PRIMARY_SUPPORT_EMAIL',
  'PRIMARY_CONTACT_EMAILS',
  'COUNTRY_CODE',
  '2FA_MODE',
] as const

const IMAGE_KINDS: ImageKind[] = ['logo', 'favicon', 'background_image']

const LEADER_ROLES: LeaderRole[] = ['captain', 'vice_captain', 'admin']

const pick = <K extends keyof Values>(values: Values, keys: readonly K[]) =>
  Object.fromEntries(keys.map((key) => [key, values[key]])) as Pick<Values, K>

function useSidebarPreview(color: string | null) {
  const { sidebarColor, foregroundColor } = usePage<{ navTree?: NavTree }>().props.navTree ?? {}

  useLayoutEffect(() => {
    if (!color) return
    applySidebarSurface({ sidebarColor: color, foregroundColor: embedTheme(color).brandText })
    return () => applySidebarSurface({ sidebarColor, foregroundColor })
  }, [color, sidebarColor, foregroundColor])
}

function SiteForm({ values, countries, contactGaps, admins }: GeneralShowProps) {
  const form = useForm(pick(values, SITE_KEYS))
  const sidebarPreview = parseHex(form.data.SIDEBAR_COLOR)
  const previewing = sidebarPreview !== null && sidebarPreview !== parseHex(values.SIDEBAR_COLOR)
  useSidebarPreview(sidebarPreview)
  const errors = fieldErrors(form.errors)
  const contacts = form.data.PRIMARY_CONTACT_EMAILS.split(',').filter(Boolean)
  const adminOptions = [...new Set([...admins, ...contacts])].map((email) => ({ value: email, label: email }))

  return (
    <SettingsCard heading={t('site.heading')}>
      <SettingsForm form={form} action="/app/settings/general">
        <TextInput label={t('site.app_name')} {...textField(form, 'APP_NAME')} />
        <TextInput
          label={t('site.sidebar_colour')}
          description={t(previewing ? 'site.sidebar_colour_previewing' : 'site.sidebar_colour_hint')}
          {...textField(form, 'SIDEBAR_COLOR')}
          leadingContent={
            <input
              type="color"
              aria-label={t('site.sidebar_colour_picker')}
              value={sidebarPreview?.toLowerCase() ?? '#000000'}
              onChange={(event) => form.setData('SIDEBAR_COLOR', event.target.value)}
              className="tw:size-6 tw:cursor-pointer tw:rounded tw:border-0 tw:bg-transparent tw:p-0"
            />
          }
        />
        <SelectField
          name="MINIMUM_TIME_BETWEEN_CONTACTS"
          label={t('site.contact_gap')}
          options={contactGaps}
          value={form.data.MINIMUM_TIME_BETWEEN_CONTACTS}
          onValueChange={(value) => form.setData('MINIMUM_TIME_BETWEEN_CONTACTS', value)}
          errors={errors}
        />
        <TextInput type="email" label={t('site.support_email')} {...textField(form, 'PRIMARY_SUPPORT_EMAIL')} />
        <MultiSelectField
          name="PRIMARY_CONTACT_EMAILS"
          label={t('site.primary_contacts')}
          placeholder={t('site.primary_contacts_placeholder')}
          searchPlaceholder={t('site.primary_contacts_search')}
          noResultsText={t('site.primary_contacts_none')}
          options={adminOptions}
          value={contacts}
          onValueChange={(emails) => form.setData('PRIMARY_CONTACT_EMAILS', emails.join(','))}
          errors={errors}
        />
        <SelectField
          search
          name="COUNTRY_CODE"
          label={t('site.country')}
          options={countries}
          value={form.data.COUNTRY_CODE}
          onValueChange={(value) => form.setData('COUNTRY_CODE', value)}
          errors={errors}
        />
        <RadioGroupField
          label={t('site.two_factor')}
          value={form.data['2FA_MODE']}
          onValueChange={(value) => form.setData('2FA_MODE', value)}
          options={[
            { value: 'optional', label: t('site.two_factor_optional') },
            { value: 'required', label: t('site.two_factor_required') },
          ]}
        />
      </SettingsForm>
    </SettingsCard>
  )
}

function ImageRow({ kind, current }: { kind: ImageKind; current: string | null }) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const inputId = useId()
  const errorId = useId()

  const upload = (event: FormEvent) => {
    event.preventDefault()
    router.post(
      '/app/settings/branding_images',
      { kind, file },
      {
        forceFormData: true,
        preserveScroll: true,
        onStart: () => {
          setUploading(true)
          setError(undefined)
        },
        onFinish: () => setUploading(false),
        onSuccess: () => setFile(null),
        onError: (errors) => setError(errors[kind] ?? Object.values(errors)[0]),
      }
    )
  }

  return (
    <li className="tw:border-t tw:border-gray-cool-100 tw:px-5 tw:py-4 tw:first:border-t-0">
      <form onSubmit={upload} className="tw:flex tw:flex-wrap tw:items-center tw:gap-4">
        <div className="tw:flex tw:h-16 tw:w-36 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50">
          {current ? (
            <img src={current} alt={t(`images.${kind}`)} className="tw:max-h-full tw:max-w-full tw:object-contain" />
          ) : (
            <span className="tw:px-2 tw:text-center tw:text-xs tw:text-gray-cool-500">{t('images.none')}</span>
          )}
        </div>
        <div className="tw:flex tw:min-w-0 tw:grow tw:flex-col tw:gap-2">
          <label htmlFor={inputId} className="tw:m-0 tw:text-sm tw:font-medium tw:text-gray-cool-900">
            {t(`images.${kind}`)}
          </label>
          <input
            id={inputId}
            key={current}
            type="file"
            accept="image/*"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className="tw:text-sm tw:text-gray-cool-700 tw:file:mr-3 tw:file:cursor-pointer tw:file:rounded-lg tw:file:border tw:file:border-gray-cool-300 tw:file:bg-white tw:file:px-3 tw:file:py-2 tw:file:text-sm tw:file:font-medium"
          />
          <div role="status">
            {error && (
              <p id={errorId} className="tw:m-0 tw:text-sm tw:text-red-600">
                {error}
              </p>
            )}
          </div>
        </div>
        <Button type="submit" rank="secondary" disabled={!file || uploading}>
          {t('images.upload')}
        </Button>
      </form>
    </li>
  )
}

function ContentForm({ values, keys, rich }: { values: Values; keys: ContentKey[]; rich: ContentKey }) {
  const form = useForm(pick(values, keys))
  const errors = fieldErrors(form.errors)

  return (
    <SettingsForm form={form} action="/app/settings/general">
      {keys.map((key) =>
        key === rich ? (
          <RichTextEditorField
            key={key}
            name={key}
            label={t(`content.${key.toLowerCase()}`)}
            description={t(`content.${key.toLowerCase()}_hint`, { defaultValue: '' }) || undefined}
            value={form.data[key]}
            onChange={(value) => form.setData(key, value)}
            errors={errors}
          />
        ) : (
          <TextInput key={key} label={t(`content.${key.toLowerCase()}`)} {...textField(form, key)} />
        )
      )}
    </SettingsForm>
  )
}

type LeaderTaggingFields = { enabled: boolean } & Record<LeaderRole, { value: string; label: string }[]>

function LeaderTaggingForm({ leaderTagging }: { leaderTagging: LeaderTagging }) {
  const toOptions = (tags: string[]) => tags.map((tag) => ({ value: tag, label: tag }))
  const { control, handleSubmit, watch, formState, reset } = useHookForm<LeaderTaggingFields>({
    defaultValues: {
      enabled: leaderTagging.enabled,
      captain: toOptions(leaderTagging.captain),
      vice_captain: toOptions(leaderTagging.vice_captain),
      admin: toOptions(leaderTagging.admin),
    },
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const save = handleSubmit((data) => {
    const tags = (role: LeaderRole) => data[role].map((option) => option.value)
    router.patch(
      '/app/settings/leader_tagging',
      {
        leader_tagging: {
          auto_tag_leaders: data.enabled,
          captain_tags: tags('captain'),
          vice_captain_tags: tags('vice_captain'),
          admin_tags: tags('admin'),
        },
      },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
        onSuccess: () => {
          reset(data)
          setSaved(true)
        },
      }
    )
  })

  return (
    <form onSubmit={save}>
      <div className="tw:flex tw:flex-col tw:gap-5 tw:p-5">
        <div className="tw:flex tw:flex-wrap tw:items-center">
          <Checkbox control={control} name="enabled">
            {t('leader_tagging.enabled')}
          </Checkbox>
        </div>
        {watch('enabled') &&
          LEADER_ROLES.map((role) => (
            <TagsSelect key={role} control={control} name={role} displayName={t(`leader_tagging.${role}`)} />
          ))}
      </div>
      <SettingsFormFooter disabled={!formState.isDirty || saving} saved={saved && !formState.isDirty} />
    </form>
  )
}

export default function GeneralShow(props: GeneralShowProps) {
  const { values, images, switches, leaderTagging } = props
  const termsOfUseRequired = switches.some(({ key, checked }) => key === 'REQUIRE_TERMS_OF_USE' && checked)

  return (
    <ConfigurationsLayout>
      <SiteForm {...props} />
      <SettingsCard heading={t('images.heading')}>
        <ul className="tw:m-0 tw:list-none tw:p-0">
          {IMAGE_KINDS.map((kind) => (
            <ImageRow key={kind} kind={kind} current={images[kind]} />
          ))}
        </ul>
      </SettingsCard>
      <SettingsCard heading={t('features.heading')} description={t('features.description')}>
        <SettingsSwitches action="/app/settings/general" switches={switches} t={t} />
      </SettingsCard>
      {leaderTagging && (
        <SettingsCard
          heading={
            <span className="tw:flex tw:items-center tw:gap-2">
              {t('leader_tagging.heading')}
              <Badge color="info">{t('leader_tagging.beta')}</Badge>
            </span>
          }
          description={t('leader_tagging.description')}
        >
          <LeaderTaggingForm leaderTagging={leaderTagging} />
        </SettingsCard>
      )}
      <SettingsCard heading={t('content.heading')}>
        <Accordion type="multiple" className="tw:px-5">
          {termsOfUseRequired && (
            <SettingsAccordionItem value="terms_of_use" trigger={t('content.terms_of_use')}>
              <ContentForm
                values={values}
                keys={['TERMS_OF_USE_HEADLINE', 'TERMS_OF_USE_DECLARATION', 'TERMS_OF_USE_CONTENT']}
                rich="TERMS_OF_USE_CONTENT"
              />
            </SettingsAccordionItem>
          )}
          <SettingsAccordionItem value="group_page" trigger={t('content.group_page')}>
            <ContentForm values={values} keys={['DEFAULT_GROUP_PAGE_CONTENT']} rich="DEFAULT_GROUP_PAGE_CONTENT" />
          </SettingsAccordionItem>
          <SettingsAccordionItem value="unsubscribe" trigger={t('content.unsubscribe')}>
            <ContentForm
              values={values}
              keys={['UNSUBSCRIBE_LINK_TEXT', 'UNSUBSCRIBE_PAGE_HEADLINE', 'UNSUBSCRIBE_PAGE_CONTENT']}
              rich="UNSUBSCRIBE_PAGE_CONTENT"
            />
          </SettingsAccordionItem>
        </Accordion>
      </SettingsCard>
    </ConfigurationsLayout>
  )
}
